import Content from "../../Content";
import { useDispatch } from "react-redux";
import { FilterItemCountsWithSelect } from "types";
import VLAccordian from "views/components/Accordian";
import { useNavigate, useParams } from "react-router";
import { RefObject, useCallback, useMemo, useState } from "react";
import { clearFilters } from "redux/SingleDataset/actions";
import { checkIsUnselectable } from "helpers/utility/utilities";
import EnrichmentTeaser from "views/components/EnrichmentTeaser";
import ContentBodyLoader from "../../Content/ContentBody/ContentBodyLoader";

import styles from "./style.module.scss";

const ENRICHMENT_MODELS_SECTION_ID = "vl-enrichment-1";

interface EnrichmentModelsSectionContentProps {
  sources: FilterItemCountsWithSelect[];
  models: FilterItemCountsWithSelect[];
  sectionHeight: number | false;
  isLoading: boolean;
}

interface EnrichmentModelsSectionProps {
  vlEnrichmentRef: RefObject<HTMLDivElement>;
  sources: FilterItemCountsWithSelect[];
  models: FilterItemCountsWithSelect[];
  sectionHeight: number | false;
  showVLEnrichment: boolean;
  isLoading: boolean;
  icon: JSX.Element;
}

const EnrichmentModelsSectionContent = ({
  models,
  sources,
  isLoading,
  sectionHeight,
}: EnrichmentModelsSectionContentProps) => {
  const initialSelectedID = sectionHeight ? ENRICHMENT_MODELS_SECTION_ID : null;
  const [selectedID, setSelectedID] = useState(initialSelectedID);
  const combinedSources = useMemo(
    () => [...models, ...sources],
    [models, sources]
  );

  const getIsUnselectable = useCallback(
    (value: FilterItemCountsWithSelect) =>
      checkIsUnselectable(value, models, sources),
    [sources, models]
  );

  return (
    <div
      className={styles.section}
      style={{ height: !!sectionHeight ? sectionHeight : "max-content" }}
    >
      <Content
        data={models}
        searchDisabled
        type={"models"}
        title={"Models"}
        isLoading={isLoading}
        selectedID={selectedID}
        sources={combinedSources}
        totalCount={models.length}
        setSelectedID={setSelectedID}
        sectionType={"vl-enrichment"}
        id={ENRICHMENT_MODELS_SECTION_ID}
        getIsUnselectable={getIsUnselectable}
      />
    </div>
  );
};

const EnrichmentModelsSection = ({
  sectionHeight = false,
  showVLEnrichment,
  vlEnrichmentRef,
  isLoading,
  sources,
  models,
  icon,
}: EnrichmentModelsSectionProps) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { datasetId } = useParams();

  const handleTryEnrichmentClick = () => {
    dispatch(clearFilters() as any);
    navigate(`/dataset/${datasetId!}/enrich`);
  };

  const handleDisplay = () => {
    if (isLoading && !models.length) {
      return (
        <div className={styles.loadingBox}>
          <ContentBodyLoader center />
        </div>
      );
    } else {
      return (
        <EnrichmentModelsSectionContent
          models={models}
          sources={sources}
          isLoading={isLoading}
          sectionHeight={sectionHeight}
        />
      );
    }
  };

  return showVLEnrichment || isLoading ? (
    <div className={styles.vlEnrichmentWrapper} ref={vlEnrichmentRef}>
      <VLAccordian
        icon={icon}
        key="Enrichment models"
        title="Enrichment models"
        allowResize={models.length > 5}
        id={ENRICHMENT_MODELS_SECTION_ID}
        isLoading={isLoading && !!models.length}
      >
        {handleDisplay()}
      </VLAccordian>
    </div>
  ) : (
    <EnrichmentTeaser
      onTryEnrichmentClick={handleTryEnrichmentClick}
      vlEnrichmentRef={vlEnrichmentRef}
    />
  );
};
export default EnrichmentModelsSection;
